import { AxiosResponse } from 'axios';

import { USERS_ACTION_TYPE, USERS_ROLE_TYPE } from '../../types';

import {
  AccountResponse,
  BankAccountResponse,
  HintType,
  PageType,
  ProfileResponse,
  SetProfileRequest,
  UploadDocResponse,
  User,
} from './dto/Accounts';
import BaseApi, { AuthApi, RetryBaseApi } from './BaseApi';

const accountApi = {
  getBanksAccounts(): Promise<AxiosResponse<BankAccountResponse[]>> {
    return BaseApi.get('/api/v1/personal/bank/accounts');
  },

  getProfileInfo(token: string): Promise<AxiosResponse<User>> {
    return AuthApi.get('/api/auth/v1/profile', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  getAccountInfo(token: string): Promise<AxiosResponse<AccountResponse>> {
    const headers = {} as Record<string, any>;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return BaseApi.get('/api/v1/accounts/default', { headers });
  },

  setProfileInfo(
    token: string,
    payload: SetProfileRequest
  ): Promise<AxiosResponse<ProfileResponse>> {
    return AuthApi.post('/api/auth/v1/profile/initialize', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  setHintInfo(
    token: string,
    payload: { type: HintType; hint: PageType }
  ): Promise<AxiosResponse<ProfileResponse>> {
    return AuthApi.post('/api/auth/v1/profile/hint/status', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  setProfilePassword(
    token: string,
    payload: { password: string }
  ): Promise<AxiosResponse<ProfileResponse>> {
    return AuthApi.post('/api/auth/v1/profile/password', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  getUploadOnboardingDoc(token: string) {
    return RetryBaseApi.get(`/api/v1/personal/document/onboarding`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'arraybuffer',
    });
  },

  uploadProfileDoc(
    token: string,
    payload: {
      type: string;
      doc: Blob;
    }
  ): Promise<AxiosResponse<UploadDocResponse>> {
    const formData = new FormData();
    formData.append('document', payload.doc);
    return RetryBaseApi.post(
      `/api/v1/personal/document/upload?type=${payload.type}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  setPubKey(
    token: string,
    payload: {
      pubKey: string;
    }
  ): Promise<AxiosResponse<UploadDocResponse>> {
    return RetryBaseApi.post(`/api/v1/personal/set/key`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  getAccessionDoc(token: string): Promise<AxiosResponse<ArrayBuffer>> {
    return RetryBaseApi.get(`/api/v1/personal/document/accession`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'arraybuffer',
    });
  },

  getAccessionDocText(token: string): Promise<AxiosResponse<string>> {
    return RetryBaseApi.get(`/api/v1/personal/document/accession/text`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  setPassword(token: string, password: string) {
    return AuthApi.post(
      '/api/auth/v1/profile/password',
      { password },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  getUserList(token: string): Promise<AxiosResponse<User[]>> {
    return AuthApi.get('/api/auth/v1/administration/company/users', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  setUserAction(token: string, id: number, action: USERS_ACTION_TYPE) {
    return AuthApi.post(
      '/api/auth/v1/administration/company/user/action',
      { userId: id, action },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  setUserRole(token: string, id: number, role: USERS_ROLE_TYPE) {
    return AuthApi.post(
      '/api/auth/v1/administration/company/user/change/role',
      { userId: id, role },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  createUser(
    token: string,
    orgId: number,
    name: string,
    email: string,
    role: USERS_ROLE_TYPE
  ) {
    return AuthApi.post(
      '/api/auth/v1/administration/company/create/invite',
      { orgId, name, email, role },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  updateUser(
    token: string,
    fullName?: string,
    email?: string,
    enableOrderNotification?: boolean,
    enableOnlyConfirmedNotification?: boolean
  ) {
    return AuthApi.post(
      '/api/auth/v1/profile/update',
      {
        fullName,
        email,
        enableOrderNotification,
        enableOnlyConfirmedNotification,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },

  changePassword(token: string, password?: string, newPassword?: string) {
    return AuthApi.post(
      '/api/auth/v1/profile/password/change',
      { password, newPassword },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  },
};

export default accountApi;
